<template>
    <div class="create-stek">
        <Dialog
            header="Name your Stek"
            @keyup.esc="cancelCreatingStek"
            @hide="cancelCreatingStek"
            v-model:visible="displayCreateStekModal"
            class="create-stek-dialog"
            :closable="false"
            :modal="true"
            :draggable="false"
        >
            <div v-if="errors.length">
                <div v-for="error in errors" :key="error">
                    <Message severity="error" :closable="false">
                        {{ error }}
                    </Message>
                </div>
            </div>
            <form
                v-on:submit.prevent="createStek"
                class="create-stek-form"
                aria-label="Create new Stek form"
            >
                <div class="field-info">
                    <p>
                        A Stek is a collaborative workspace that contains your
                        organization's domains, products, and processes. It
                        serves as the central hub for all your intelligent
                        automation.
                    </p>
                    <p>
                        It's your own central place within the MyStekz platform,
                        so let's get started!
                    </p>
                </div>
                <Fluid class="field">
                    <label for="stekName" class="required-field">Name</label>
                    <InputText
                        id="stekName"
                        v-model="stekName"
                        aria-required="true"
                        :aria-invalid="!!nameError"
                        :class="{ 'p-invalid': nameError }"
                        autofocus
                    />
                    <small v-if="nameError" id="stekName-help" class="p-error">
                        {{ nameError }}
                    </small>
                    <small class="p-d-block">
                        The name cannot be changed later.
                    </small>
                </Fluid>
                <Fluid class="field">
                    <label for="stekFqdn">Website</label>
                    <InputText
                        id="stekFqdn"
                        v-model="fqdnInput"
                        @update:modelValue="handleFqdnChange"
                        aria-required="false"
                        :aria-invalid="!!fqdnError"
                        :class="{ 'p-invalid': fqdnError }"
                        placeholder="e.g. stekz.com"
                    />
                    <small v-if="fqdnError" class="p-error">
                        {{ fqdnError }}
                    </small>
                    <small class="p-d-block">
                        Enter your domain name (e.g. example.com) or full URL
                        (e.g. https://example.com). This is used to improve
                        suggestions given.
                    </small>
                </Fluid>
            </form>
            <template #footer>
                <Button
                    label="Cancel"
                    @click="cancelCreatingStek"
                    class="p-button-secondary"
                    :disabled="isLoading"
                />
                <Button
                    label="Create"
                    @click="createStek"
                    :loading="isLoading"
                    :disabled="!isValid || isLoading || !!fqdnError"
                    class="p-button-primary"
                />
            </template>
        </Dialog>
    </div>
</template>

<style lang="scss">
.create-stek-dialog {
    width: 90vw;
    max-width: 1080px;

    @media screen and (min-width: 768px) {
        width: 50vw;
    }
}
</style>

<style lang="scss" scoped>
.create-stek-form {
    .field {
        margin-bottom: 1.5rem;

        label {
            display: block;
            margin-bottom: 0.5rem;
        }
    }

    .field-info {
        background-color: var(--stekz-light-gray);
        padding: 1rem;
        border-left: 4px solid var(--stekz-primary-color);
        margin-bottom: 1rem;

        p:not(:last-child) {
            margin-bottom: 1rem;
        }
    }

    .required-field::after {
        content: "*";
        color: var(--red-500);
        margin-left: 0.25rem;
    }

    small {
        display: block;
        margin-top: 0.25rem;
        color: var(--text-color-secondary);

        &.p-error {
            color: var(--red-500);
        }
    }
}
</style>

<script setup lang="ts">
import { ref, computed } from "vue";
import router from "@/router";
import { validateName, validateAndCleanFqdn } from "@/utils";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useSteksStore } from "@/stores/steks-store";

const displayCreateStekModal = ref(true);
const stekName = ref("");
const fqdnInput = ref("");
const stekFqdn = ref("");
const fqdnError = ref<string | undefined>();
const stekLogo = ref<File | null>(null);
const errors = ref<string[]>([]);
const isLoading = ref(false);
const { organization } = useOrganizationManager();
const { create } = useSteksStore();

const handleFqdnChange = (value: string | undefined) => {
    const result = validateAndCleanFqdn(value || "");
    fqdnError.value = result.error;
    stekFqdn.value = result.value;
};

const nameError = computed(() => {
    if (!stekName.value) return "";
    const errors = validateName(stekName.value);
    return errors.length ? errors[0] : "";
});

const isValid = computed(() => {
    return stekName.value && !nameError.value;
});

const cancelCreatingStek = () => {
    displayCreateStekModal.value = false;
    router.back();
};

const createStek = async () => {
    if (!isValid.value) return;
    if (!organization.value?.name) return;
    if (fqdnError.value) return;

    try {
        isLoading.value = true;
        await create(
            stekName.value,
            stekFqdn.value || undefined,
            stekLogo.value || undefined
        );
        router.push({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stekName.value,
            },
        });
    } catch (error) {
        try {
            errors.value = [];
            // @ts-expect-error error may not have a detail property
            // eslint-disable-next-line no-unsafe-optional-chaining
            for (const detail of error?.detail) {
                errors.value.push(detail.message);
            }
        } catch {
            errors.value = [(error as Error).message];
        }
    } finally {
        isLoading.value = false;
    }
};
</script>
